"use client";

import request from "@/lib/request";
import { usePathname } from "@/i18n/navigation";
import { useQuery } from "@tanstack/react-query";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

export interface CurrencyEntity {
  baseCurrency?: string;
  createPerson?: string;
  createTime?: string;
  currencySymbol?: string;
  id?: string;
  isActive?: number;
  isBaseCurrency?: number;
  isDelete?: number;
  memberId?: string;
  rate?: number;
  targetCurrencyName?: string;
  targetCurrency?: string;
  updatePerson?: string;
  updateTime?: string;
}

const queryAllCurrency = (): Promise<CurrencyEntity[]> => {
  return request({
    url: "/ipmsaccount/finCurrencyQueryFacade/ecommerce/queryAllCurrency",
    data: {},
  });
};

interface CurrencyContextValue {
  baseCurrencySymbol: string;
}

const CurrencyContext = createContext<CurrencyContextValue>({
  baseCurrencySymbol: "",
});

export function CurrencyProvider(props: { children: React.ReactNode }) {
  const pathname = usePathname();
  const prevPathnameRef = useRef<string | null>(null);

  const { data: currencyList = [], refetch } = useQuery({
    queryKey: ["queryAllCurrency"],
    queryFn: queryAllCurrency,
    staleTime: 5 * 60 * 1000,
  });

  const baseCurrencySymbol = useMemo(() => {
    return (
      currencyList.find((item) => item.isBaseCurrency === 1)?.currencySymbol ??
      ""
    );
  }, [currencyList]);

  useEffect(() => {
    if (prevPathnameRef.current === null) {
      prevPathnameRef.current = pathname;
      return;
    }
    const pathChanged = prevPathnameRef.current !== pathname;
    if (pathChanged) {
      prevPathnameRef.current = pathname;
    }
    if (pathChanged && baseCurrencySymbol === "") {
      void refetch();
    }
  }, [pathname, baseCurrencySymbol, refetch]);

  const value = useMemo<CurrencyContextValue>(() => {
    return {
      baseCurrencySymbol,
    };
  }, [baseCurrencySymbol]);

  return (
    <CurrencyContext.Provider value={value}>
      {props.children}
    </CurrencyContext.Provider>
  );
}

export const useCurrencyContext = () => useContext(CurrencyContext);
